import { useCallback, useMemo } from "react"
import ProductDesign from "../../Products/ProductDesign"
import { useAppDispatch } from "../../../Common/_hooks/useAppDispatch"
import { RemoveProductListing } from "../../../UserData/_actions/Store/StoreProductListingActions"
import type { UserProductDesignStore } from "../../../UserData/_stores/UserProductDesignStore"
import { useAppSelector } from "../../../Common/_hooks/useAppSelector"
import { Button, IconButton, Stack, Typography } from "@mui/material"
import { useTranslation } from "react-i18next"
import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import type { ProductLayoutType } from "../../../UserData/_stores/UserPrefsStore"
import type { StoreListingValidationError } from "./Utils/StoreListingValidation"

interface Props {
  view: ProductLayoutType
  item: UserProductDesignStore
  onEdit: (id: number) => void
  allowProductRemoval?: boolean
  validationErrors?: StoreListingValidationError[]
}

export default function ProductListingEditCard(props: Props) {
  const [t] = useTranslation('account-v2')
  const dispatch = useAppDispatch()

  const storePreset = useAppSelector(state => state.get('userData').get('storePresets').get(props.item.get('id_product')))
  const isModified = useAppSelector(state => state.get('UIData').get('account').get('storeProductListings').get(String(props.item.get('id')))?.get('is_modified') ?? false)
  const listingTitle = useAppSelector(state => state.get('UIData').get('account').get('storeProductListings').get(String(props.item.get('id')))?.get('title'))
  const validationError = props.validationErrors?.[0]

  const onEdit = useCallback(() => {
    props.onEdit(props.item.get('id'))
  }, [props.item.get('id'), props.onEdit])

  const onRemove = useCallback(() => {
    dispatch(RemoveProductListing(String(props.item.get('id'))))
  }, [dispatch, props.item.get('id')])

  const caption = useMemo(() => {
    return <Stack direction="row" alignItems="center" spacing={0.5}>
      {validationError
        ? <ErrorOutlineIcon fontSize="small" color="error" />
        : (isModified || (storePreset && !storePreset.get('default')) ? <CheckCircleIcon fontSize="small" color="success" /> : null)}
      <Typography variant="caption" sx={{ color: validationError ? 'error.main' : 'secondary.main' }}>
        {validationError
          ? (validationError.variantLimit
            ? t('Variation limit exceeded')
            : t('Listing needs attention'))
          : isModified
            ? t('Changes saved')
            : (storePreset && !storePreset.get('default'))
              ? t('Using preset')
              : t('Details & Prices created by Art of Where')
        }
      </Typography>
    </Stack>
  }, [validationError, isModified, storePreset, props.view, t])

  return <ProductDesign
    item={props.item}
    view={props.view}
    title={listingTitle}
    caption={props.view === 'list' ? caption : undefined}
    error={!!validationError}
    actions={<Stack direction="column" alignItems="flex-start" spacing={1}>
      <Button
        variant="outlined"
        color={validationError ? 'error' : 'secondary'}
        onClick={onEdit}
      >{t('Edit')}</Button>
      { props.view === 'grid' ? caption : null}
    </Stack>}
    secondaryActions={props.allowProductRemoval === false ? undefined : <IconButton
      onClick={onRemove}
    ><CancelIcon /></IconButton>}
  />
}
